import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

const ScopeModal = ({ 
  show, 
  onHide, 
  onSubmit, 
  isEditing, 
  initialData = { scopeName: '', scopeInformation: '• ' } 
}) => {
  // Force bullet point in initial data when component mounts
  if (!initialData.scopeInformation || initialData.scopeInformation.trim() === '') {
    initialData.scopeInformation = '• ';
  } else if (!initialData.scopeInformation.includes('•')) {
    initialData.scopeInformation = '• ' + initialData.scopeInformation;
  }
  
  const [formData, setFormData] = useState(initialData);
  const textareaRef = useRef(null);
  
  // Reset form data when the modal opens/closes or when initial data changes
  useEffect(() => {
    if (show) {
      console.log('ScopeModal shown with initialData:', initialData);
      
      // If editing an existing scope, use its data
      if (isEditing && initialData.scopeInformation) {
        console.log('Editing mode - using initialData');
        setFormData(initialData);
      } else {
        // For a new scope, start with a bullet point
        console.log('Add mode - adding bullet point');
        // Force a bullet point at the beginning if not already there
        const scopeInfo = initialData.scopeInformation || '';
        const hasLeadingBullet = scopeInfo.trimStart().startsWith('•');
        
        setFormData({
          ...initialData,
          scopeInformation: hasLeadingBullet ? scopeInfo : '• '
        });
      }
    }
  }, [show, initialData, isEditing]);
  
  // Set focus to the textarea after it renders
  useEffect(() => {
    if (show && textareaRef.current) {
      const length = textareaRef.current.value.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [show]);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle keydown events in the scope information textarea
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default enter behavior
      
      const cursorPosition = e.target.selectionStart;
      const currentValue = e.target.value;
      
      // Insert new line with bullet point at cursor position
      const newValue = 
        currentValue.substring(0, cursorPosition) + 
        '\n• ' + 
        currentValue.substring(cursorPosition);
      
      setFormData(prev => ({
        ...prev,
        scopeInformation: newValue
      }));
      
      // Schedule setting the cursor position after React updates the DOM
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = cursorPosition + 3; // Position after the bullet point
          textareaRef.current.selectionEnd = cursorPosition + 3;
        }
      }, 0);
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };
  
  return (
    <Modal show={show} onHide={onHide} backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Edit Scope' : 'Add Scope'}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Scope Name</Form.Label>
            <Form.Control
              type="text"
              name="scopeName"
              value={formData.scopeName}
              onChange={handleChange}
              required
              placeholder="Enter scope name (e.g., Kitchen, Bathroom, Electrical)"
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Scope Information</Form.Label>
            <Form.Control
              as="textarea"
              rows={7}
              name="scopeInformation"
              value={formData.scopeInformation}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              ref={textareaRef}
              required
              placeholder="Describe the scope of work..."
            />
            <Form.Text className="text-muted">
              Press Enter to add a new bullet point. Each bullet point will appear on a new line in the proposal.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            {isEditing ? 'Update Scope' : 'Add Scope'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ScopeModal;
